import React, { useState, useEffect } from 'react';
import './LandingPage.css';
import SearchBar from '../components/SearchBar';
import TierCard from '../components/TierCard';
import ApiService from '../services/api';
import { trackPageView, trackSearch as gtmTrackSearch, trackTierSelected as gtmTrackTierSelected, trackCheckoutInitiated as gtmTrackCheckoutInitiated, trackDropOff } from '../utils/gtm';
import * as Analytics from '../utils/analytics';

function LandingPage() {
  const [selectorType, setSelectorType] = useState('email');
  const [selectorValue, setSelectorValue] = useState('');
  const [preSearchResult, setPreSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDeepDiveForm, setShowDeepDiveForm] = useState(false);
  const [deepDiveData, setDeepDiveData] = useState({
    fullName: '',
    company: '',
    location: '',
    username: ''
  });

  // Track page view on mount
  useEffect(() => {
    trackPageView('/', 'Landing Page - OSIN7');
  }, []);

  const handleInitialSearch = async () => {
    if (!selectorValue.trim()) {
      alert('Please enter a valid email or phone number');
      return;
    }

    setIsSearching(true);
    try {
      const result = await ApiService.presearch(selectorValue, selectorType);
      setPreSearchResult({
        count: result.count,
        selectorType: result.selector_type,
        selectorValue: result.selector
      });
      
      // Track successful search (both GTM and Firebase Analytics)
      gtmTrackSearch(result.selector_type, result.count);
      Analytics.trackSearch(result.selector_type, result.count);
    } catch (error) {
      console.error('Pre-search failed:', error);
      alert(`Search failed: ${error.message}. Please try again.`);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectTier = (tier) => {
    // Track tier selection (both GTM and Firebase Analytics)
    gtmTrackTierSelected(tier, preSearchResult.selectorType, preSearchResult.count);
    Analytics.trackTierSelected(tier, preSearchResult.selectorType, preSearchResult.count);
    
    if (tier === 'deepdive') {
      setShowDeepDiveForm(true);
    } else {
      // Basic tier - proceed to checkout
      handleProceedToCheckout('basic');
    }
  };

  const handleDeepDiveSubmit = () => {
    handleProceedToCheckout('deepdive');
  };

  const handleProceedToCheckout = async (tier) => {
    try {
      const tierType = tier === 'basic' ? 'tier1' : 'tier2';
      
      const params = {
        tier: tierType,
        selector: selectorValue,
        selectorType: selectorType
      };

      // Add optional fields for tier2
      if (tier === 'deepdive') {
        if (deepDiveData.fullName) params.fullName = deepDiveData.fullName;
        if (deepDiveData.company) params.company = deepDiveData.company;
      }

      // Create checkout session and get Stripe URL
      const result = await ApiService.createCheckoutSession(params);
      
      // Store report_id in sessionStorage so we can use it after payment
      sessionStorage.setItem('pending_report_id', result.report_id);
      
      // Track checkout initiation (both GTM and Firebase Analytics)
      gtmTrackCheckoutInitiated(result.report_id, tierType, selectorType);
      Analytics.trackCheckoutInitiated(result.report_id, tierType, selectorType);
      
      // Redirect to Stripe checkout
      window.location.href = result.url;
    } catch (error) {
      console.error('Checkout failed:', error);
      alert(`Payment setup failed: ${error.message}. Please try again.`);
    }
  };

  const resetSearch = () => {
    // Track drop-off if user resets after seeing results
    if (preSearchResult) {
      const stage = showDeepDiveForm ? 'deep_dive_form' : 'tier_selection';
      trackDropOff(stage, 'new_search_clicked');
    }
    
    setPreSearchResult(null);
    setShowDeepDiveForm(false);
    setSelectorValue('');
    setDeepDiveData({
      fullName: '',
      company: '',
      location: '',
      username: ''
    });
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <header className="landing-header">
          <h1 className="logo">OSIN7</h1>
          <p className="tagline">Discover online accounts and data in seconds</p>
        </header>

        {!preSearchResult && !showDeepDiveForm && (
          <div className="search-section">
            <SearchBar
              selectorType={selectorType}
              setSelectorType={setSelectorType}
              selectorValue={selectorValue}
              setSelectorValue={setSelectorValue}
              onSearch={handleInitialSearch}
              isSearching={isSearching}
            />
          </div>
        )}

        {showDeepDiveForm && (
          <div className="deep-dive-form">
            <button className="back-button" onClick={() => setShowDeepDiveForm(false)}>
              ← Back to Tiers
            </button>
            <h2>Deep Dive Report - Additional Information</h2>
            <p className="form-subtitle">
              Help us find more by providing additional details (optional)
            </p>
            
            <div className="form-group">
              <label>Primary Selector *</label>
              <input
                type="text"
                value={selectorValue}
                placeholder={selectorType === 'email' ? 'email@example.com' : '+1234567890'}
                disabled
              />
            </div>

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={deepDiveData.fullName}
                onChange={(e) => setDeepDiveData({...deepDiveData, fullName: e.target.value})}
                placeholder="John Doe"
              />
            </div>

            <div className="form-group">
              <label>Company</label>
              <input
                type="text"
                value={deepDiveData.company}
                onChange={(e) => setDeepDiveData({...deepDiveData, company: e.target.value})}
                placeholder="Company Inc."
              />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={deepDiveData.location}
                onChange={(e) => setDeepDiveData({...deepDiveData, location: e.target.value})}
                placeholder="New York, NY"
              />
            </div>

            <div className="form-group">
              <label>Known Username</label>
              <input
                type="text"
                value={deepDiveData.username}
                onChange={(e) => setDeepDiveData({...deepDiveData, username: e.target.value})}
                placeholder="@username"
              />
            </div>

            <button 
              className="proceed-button"
              onClick={handleDeepDiveSubmit}
            >
              Proceed to Payment ($5)
            </button>
          </div>
        )}

        {preSearchResult && !showDeepDiveForm && (
          <div className="pre-search-result">
            <div className="result-card">
              <div className="result-icon"><i className="bi-bullseye"></i></div>
              <h2>We Found Results!</h2>
              <p className="result-count">
                {preSearchResult.count === 0 ? (
                  'Our quick check found some leads to explore'
                ) : (
                  <>Our quick check found at least <strong>{preSearchResult.count}</strong> potential matches</>
                )}
              </p>
              <p className="result-details">
                Searching: {preSearchResult.selectorType === 'email' ? <i className="bi-envelope-at"></i> : <i className="bi-telephone"></i>} {preSearchResult.selectorValue}
              </p>

              <div className="tiers-section">
                <h3 className="tiers-subtitle">Choose Your Search Depth</h3>
                <div className="tiers-grid">
                  <TierCard
                    title="Basic Check"
                    price="$1"
                    features={[
                      'Fast 1-hop search',
                      'Social media accounts',
                      'Public profiles',
                      'Associated selectors',
                      'Instant results'
                    ]}
                    onSelect={() => handleSelectTier('basic')}
                    disabled={false}
                    isPopular={true}
                  />
                  {/* Deep Dive tier temporarily hidden
                  <TierCard
                    title="Deep Dive"
                    price="$5"
                    features={[
                      'Everything in Basic',
                      '2-hop recursive search',
                      'Premium data sources',
                      'Breach database search',
                      'AI-powered summary',
                      'Up to 10 min search'
                    ]}
                    onSelect={() => handleSelectTier('deepdive')}
                    disabled={false}
                    isPopular={true}
                  />
                  */}
                </div>
              </div>

              <button className="cancel-button" onClick={resetSearch}>
                Start New Search
              </button>
              <p className="result-note">
                <i className="bi-lightbulb"></i> If we find zero results after payment, you'll get a full refund
              </p>
            </div>
          </div>
        )}

        <footer className="landing-footer">
          <p><i className="bi-shield-lock"></i> No account required • 72-hour report access • Secure payment via Stripe</p>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;

